// Safety screens tab: when to ask a direct screening question and where a
// positive answer hands off. The crisis protocol itself stays code-owned.

import type { SafetyRisk, SafetyRoute, SafetyScreen, TalkTrackContent } from "../../types/talkTrack";
import { SAFETY_RISKS, SAFETY_ROUTES } from "../../types/talkTrack";
import { SelectField, TextField } from "./fields";
import { nextUnitId } from "./format";
import { UnitCard } from "./UnitCard";
import { useUnitContext } from "./unitContext";

export function SafetyScreensEditor({
  topicId,
  content,
  requiresSafetyScreen,
  onChange,
}: {
  topicId: string;
  content: TalkTrackContent;
  requiresSafetyScreen: boolean;
  onChange: (next: TalkTrackContent) => void;
}) {
  const { editable } = useUnitContext();
  const setScreens = (safetyScreens: SafetyScreen[]) => onChange({ ...content, safetyScreens });
  const patch = (i: number, p: Partial<SafetyScreen>) =>
    setScreens(content.safetyScreens.map((s, j) => (j === i ? { ...s, ...p } : s)));

  function add() {
    const id = nextUnitId(content, `${topicId}.screen.`, content.safetyScreens.length + 1);
    setScreens([...content.safetyScreens, { id, when: "", question: "", route: "safety_core", risk: null, note: "" }]);
  }

  return (
    <>
      <div className="callout tt-callout">
        Screens decide <em>when</em> to ask; the crisis response itself is code-owned and never
        replaced by a talk track.
        {requiresSafetyScreen && " This topic needs at least one screen before it can be published."}
      </div>
      {content.safetyScreens.length === 0 && (
        <div className="state-box">No safety screens yet{editable ? " — add one below." : "."}</div>
      )}
      {content.safetyScreens.map((s, i) => (
        <UnitCard key={s.id} unitId={s.id} kind="Safety screen" title={s.question}
          onRemove={() => setScreens(content.safetyScreens.filter((_, j) => j !== i))}>
          <TextField label="When (the triggering signal)" editable={editable} multiline value={s.when}
            onChange={(when) => patch(i, { when })} />
          <TextField label="Screening question" editable={editable} multiline value={s.question}
            onChange={(question) => patch(i, { question })} />
          <div className="row">
            <SelectField label="Route" editable={editable} value={s.route} options={SAFETY_ROUTES}
              onChange={(route) => patch(i, { route: route as SafetyRoute })} />
            <SelectField label="Risk (asked once per conversation)" editable={editable} value={s.risk ?? ""}
              options={SAFETY_RISKS} emptyLabel="infer from wording"
              onChange={(risk) => patch(i, { risk: (risk || null) as SafetyRisk | null })} />
            <TextField label="Note" editable={editable} value={s.note} onChange={(note) => patch(i, { note })} />
          </div>
        </UnitCard>
      ))}
      {editable && <button className="btn" onClick={add}>+ Safety screen</button>}
    </>
  );
}
